import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import StatusDot from '@/components/atoms/StatusDot';
import {
    createDataTableColumnHelper,
    type DataTableColumnDef,
    timeSortFn,
} from '@/components/organisms/DataTable';
import type { CageListItem } from '@/domain/cages';
import { translateDataLabel } from '@/i18n/dataLabels';
import ActiveFishGroupCell from '@/routes/Internal/components/ActiveFishGroupCell';
import { formatDayDisplay } from '@/utils/date';
import { createCagePageUrl } from '@/utils/urlGenerator';

import { statusDotTone, statusPriority } from './cageStatus';
import BioscopeHistoryPopover from './components/BioscopeHistoryPopover';

/** A stored day as the cell shows it and as the API sends it, so a search finds either. */
function daySearchText(day: string | null): string {
    return day ? `${formatDayDisplay(day)} ${day}` : '';
}

/**
 * The pen list's columns, linking each pen, client, location and bioscope to its page.
 * `fishGroupsEnabled` is the `fish-group-tracking` flag: off, the table and its links are as
 * before fish groups.
 */
export function useCageListColumns(fishGroupsEnabled: boolean): DataTableColumnDef<CageListItem>[] {
    const { t } = useTranslation();
    const { t: tFishGroups } = useTranslation('fishGroups');
    const columnHelper = createDataTableColumnHelper<CageListItem>();

    return useMemo(() => {
        return columnHelper.columns([
            columnHelper.accessor((row) => row.id, {
                id: 'id',
                header: t('internal.cages.columns.id'),
                cell: (info) => {
                    const row = info.row.original;
                    if (!row.clientId || !row.locationId) {
                        return info.getValue();
                    }

                    // Pinned to the pen's current cohort: without `fg` the pen opens on its
                    // pen-wide legacy view. An empty pen has no cohort to pin.
                    const fishGroupId = fishGroupsEnabled ? row.activeFishGroup?.id : undefined;

                    return (
                        <Link
                            to={createCagePageUrl(
                                row.clientId,
                                row.locationId,
                                row.id,
                                '/dashboard',
                                fishGroupId
                            )}>
                            {info.getValue()}
                        </Link>
                    );
                },
            }),
            columnHelper.accessor((row) => row.name, {
                id: 'name',
                header: t('common.labels.name'),
            }),
            ...(fishGroupsEnabled
                ? [
                      columnHelper.accessor((row) => row.activeFishGroup?.name ?? null, {
                          id: 'fishGroup',
                          header: tFishGroups('locationOverview.columnHeader'),
                          cell: (info) => (
                              <ActiveFishGroupCell fishGroup={info.row.original.activeFishGroup} />
                          ),
                      }),
                  ]
                : []),
            columnHelper.accessor((row) => row.status, {
                id: 'status',
                header: t('common.labels.status'),
                cell: (info) => {
                    const status = info.getValue();
                    return <StatusDot tone={statusDotTone(status)}>{t(status)}</StatusDot>;
                },
                sortFn: (a, b) => {
                    return statusPriority(a.original.status) - statusPriority(b.original.status);
                },
            }),
            columnHelper.accessor((row) => row.clientName, {
                id: 'client',
                header: t('common.labels.client'),
                cell: (info) => {
                    const row = info.row.original;
                    if (!row.clientId || !info.getValue()) {
                        return '-';
                    }

                    return <Link to={`/internal/clients/${row.clientId}`}>{info.getValue()}</Link>;
                },
            }),
            columnHelper.accessor((row) => row.locationName, {
                id: 'location',
                header: t('common.labels.location'),
                cell: (info) => {
                    const row = info.row.original;
                    if (!row.clientId || !row.locationId || !info.getValue()) {
                        return '-';
                    }

                    return (
                        <Link to={`/c/${row.clientId}/location/${row.locationId}`}>
                            {info.getValue()}
                        </Link>
                    );
                },
            }),
            columnHelper.accessor((row) => row.cageType, {
                id: 'cageType',
                header: t('internal.cages.columns.cageType'),
                cell: (info) => info.getValue() ?? '-',
            }),
            columnHelper.accessor((row) => row.fishType, {
                id: 'fishType',
                header: t('internal.cages.columns.fishType'),
                // Translated like every other species column; the value is the DB name, which
                // is the key those translations are under.
                cell: (info) => {
                    const fishType = info.getValue();

                    return fishType ? translateDataLabel(t, 'fishTypes', fishType) : '-';
                },
            }),
            columnHelper.accessor((row) => row.externalId, {
                id: 'externalId',
                header: t('internal.cages.columns.externalId'),
                cell: (info) => info.getValue() ?? '-',
            }),
            columnHelper.accessor((row) => row.externalName, {
                id: 'externalName',
                header: t('internal.cages.columns.externalName'),
                cell: (info) => info.getValue() ?? '-',
            }),
            columnHelper.accessor((row) => row.currentBioscopeId, {
                id: 'currentBioscopeId',
                header: t('internal.cages.columns.currentBioscope'),
                cell: (info) => {
                    const value = info.getValue();
                    if (!value) {
                        return '-';
                    }

                    return <Link to={`/internal/bioscopes/${value}`}>{value}</Link>;
                },
            }),
            columnHelper.accessor(
                (row) => ({
                    items: row.bioscopeHistory,
                    toString: () =>
                        row.bioscopeHistory
                            .map(
                                (history) =>
                                    `${history.bioscopeId} ${daySearchText(history.from)} ${daySearchText(history.to)}`
                            )
                            .join(', '),
                }),
                {
                    id: 'bioscopeHistory',
                    header: t('internal.cages.columns.bioscopeHistory'),
                    cell: (info) => (
                        <BioscopeHistoryPopover history={info.row.original.bioscopeHistory} />
                    ),
                }
            ),
            columnHelper.accessor((row) => row.historicalAt, {
                id: 'historicalAt',
                header: t('internal.cages.columns.markedHistorical'),
                cell: (info) => formatDayDisplay(info.getValue()) || '-',
                sortFn: timeSortFn,
                meta: { searchText: (row) => daySearchText(row.original.historicalAt) },
            }),
            columnHelper.accessor((row) => row.firstMeasurement, {
                id: 'firstMeasurement',
                header: t('internal.cages.columns.firstMeasurement'),
                cell: (info) => formatDayDisplay(info.getValue()) || '-',
                sortFn: timeSortFn,
                meta: { searchText: (row) => daySearchText(row.original.firstMeasurement) },
            }),
            columnHelper.accessor((row) => row.lastMeasurement, {
                id: 'lastMeasurement',
                header: t('common.labels.lastMeasurement'),
                cell: (info) => formatDayDisplay(info.getValue()) || '-',
                sortFn: timeSortFn,
                meta: { searchText: (row) => daySearchText(row.original.lastMeasurement) },
            }),
        ]);
    }, [columnHelper, fishGroupsEnabled, t, tFishGroups]);
}
